/**
 * The finance verticals, and everything that differs between them.
 *
 * ONE source of truth on purpose. A vertical needs a route, a headline, meta description, an OG
 * image, calculator defaults and a row in the CRM's analytics — and if those lived in six places
 * they would drift within a month. Adding a vertical is adding an entry here.
 *
 * The funnel itself does NOT change per vertical (owner's decision, 2026-08-18). Every page runs the
 * same three steps and the same lead endpoint; what changes is the framing and where the sliders
 * start. That keeps six landing pages from becoming six funnels to maintain, while still making
 * each page feel like it was written for the person reading it.
 *
 * `slug` is load-bearing in three places — the URL, the OG image filename, and the attribution
 * value stored on the lead — so renaming one silently breaks live links, an image, and the
 * continuity of the analytics. Treat them as permanent.
 */

/**
 * `vehicleCategory` is 'car' on every entry, and that is not an oversight.
 *
 * The funnel's taxonomy is deliberately coarse — `car | motorbike | boat | caravan | other`, the
 * closed set `VEHICLE_CATEGORIES` in `backend/routers/eligibility.py` validates against. A ute, a
 * supercar and an air-cooled 911 are all "car" to it. An earlier version of this file set 'coupe',
 * 'suv', 'ute' and 'sedan', which look right and would have failed submission with a 422 for every
 * visitor arriving through a vertical page.
 *
 * The fine-grained signal is not lost: it is the landing page itself, recorded on the lead as
 * `entry_page`. That is what the CRM groups by. This field exists only so the funnel does not ask
 * a question the visitor already answered by being on the page.
 *
 * `test_vertical_config_categories_are_valid` in the backend suite pins the two together.
 */

/** Price ranges are indicative starting points for the sliders, not limits. */
export const VERTICALS = [
  {
    slug: 'jdm',
    name: 'JDM Performance',
    title: 'JDM Performance Car Finance NZ',
    metaDescription:
      'Finance for imported Japanese performance cars — Skyline, Supra, Evo, WRX STI and more. '
      + 'Check your eligibility in minutes. NZ-wide, no obligation.',
    h1: 'Finance for JDM performance cars',
    subhead:
      "Skylines, Supras, Evos, STIs — we finance the imports the banks get nervous about. "
      + "Check what you can borrow before you bid.",
    // Deliberately above the site default: someone on this page is pricing an import, not a runabout.
    defaults: { borrow_amount: 45000, deposit_amount: 8000 },
    vehicleCategory: 'car',
    ogImage: '/og-jdm.jpg',
  },
  {
    slug: 'suv',
    name: 'SUVs',
    title: 'SUV & 4WD Finance NZ',
    metaDescription:
      'Finance an SUV or 4WD in New Zealand — family wagons, 7-seaters and off-roaders. '
      + 'Check your eligibility in minutes. No obligation.',
    h1: 'Finance for SUVs and 4WDs',
    subhead:
      'The family car, the tow vehicle, the weekend escape pod. Find out what you can borrow '
      + 'before you start looking.',
    defaults: { borrow_amount: 40000, deposit_amount: 6000 },
    vehicleCategory: 'car',
    ogImage: '/og-suv.jpg',
  },
  {
    slug: 'utes',
    name: 'Utes & Trucks',
    title: 'Ute & Truck Finance NZ',
    metaDescription:
      'Finance a ute or light truck in New Zealand — Hilux, Ranger, Triton, Navara. '
      + 'Work vehicle or weekend toy. Check your eligibility in minutes.',
    h1: 'Finance for utes and trucks',
    subhead:
      'Whether it earns its keep on site or tows the boat on Saturday, find out what you can '
      + 'borrow before you talk to a dealer.',
    defaults: { borrow_amount: 45000, deposit_amount: 7000 },
    vehicleCategory: 'car',
    ogImage: '/og-utes.jpg',
  },
  {
    slug: 'supercars',
    name: 'Supercars',
    title: 'Supercar & Exotic Finance NZ',
    metaDescription:
      'Finance for supercars and exotics in New Zealand — Ferrari, Lamborghini, McLaren, Porsche. '
      + 'Discreet, specialist lending. Check your eligibility.',
    h1: 'Finance for supercars and exotics',
    subhead:
      'Specialist lending for cars the mainstream banks do not understand. Find out where you '
      + 'stand before you make the call.',
    defaults: { borrow_amount: 250000, deposit_amount: 50000 },
    vehicleCategory: 'car',
    ogImage: '/og-supercars.jpg',
  },
  {
    slug: 'european-classics',
    name: 'European Classics',
    title: 'Classic Car Finance NZ — European',
    metaDescription:
      'Finance a European classic in New Zealand — air-cooled Porsche, classic Mercedes, '
      + 'Alfa, older BMW. Check your eligibility in minutes.',
    h1: 'Finance for European classics',
    subhead:
      'Air-cooled Porsches, old Mercedes, the Alfa you have been watching for two years. '
      + 'An appreciating asset deserves finance that treats it like one.',
    defaults: { borrow_amount: 90000, deposit_amount: 20000 },
    vehicleCategory: 'car',
    ogImage: '/og-european-classics.jpg',
  },
  {
    slug: 'european',
    name: 'European',
    title: 'European Car Finance NZ',
    metaDescription:
      'Finance a new or late-model European car in New Zealand — BMW, Mercedes, Audi, VW, Volvo. '
      + 'Check your eligibility in minutes. No obligation.',
    h1: 'Finance for European cars',
    subhead:
      'BMW, Mercedes, Audi, Volvo — new or late-model. See what you can borrow before you walk '
      + 'into the dealership.',
    defaults: { borrow_amount: 70000, deposit_amount: 12000 },
    vehicleCategory: 'car',
    ogImage: '/og-european.jpg',
  },
];

/** The vertical for a slug, or null. Callers treat null as "not a vertical page". */
export function verticalBySlug(slug) {
  return VERTICALS.find((v) => v.slug === slug) || null;
}

/**
 * A human label for an entry path, for the CRM's analytics.
 *
 * Lives here rather than in the CRM so that adding a vertical updates the label automatically. The
 * CRM stores the raw PATH — labels are presentation and can be changed freely; the stored value
 * cannot, because it is what makes last month's numbers comparable to this month's.
 */
export function labelForPath(path) {
  if (!path) return 'Unknown';
  if (path === '/') return 'Home page';
  const match = path.match(/^\/finance\/([a-z-]+)/);
  if (match) {
    const v = verticalBySlug(match[1]);
    return v ? v.name : `Finance: ${match[1]}`;
  }
  return path;
}
